var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Menu, MenuItem, useTheme, } from '@mui/material';
import { ChevronLeft16, ChevronRight16 } from '@yunex/yds-icons';
import clsx from 'clsx';
import React, { Children, cloneElement, forwardRef, isValidElement, useEffect, useRef, useState, } from 'react';
import { useClickOutside } from '../../utils/hooks';
import { resolveSvgIcon } from '../../utils/internalUtils';
import MenuItemText from '../MenuItemText';
var SUBMENU_MIN_WIDTH = 100;
var useCombinedRefs = function () {
    var refs = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        refs[_i] = arguments[_i];
    }
    var targetRef = useRef();
    useEffect(function () {
        refs.forEach(function (ref) {
            if (!ref) {
                return;
            }
            if (typeof ref === 'function') {
                ref(targetRef.current);
            }
            else {
                ref.current = targetRef.current; // eslint-disable-line no-param-reassign
            }
        });
    }, [refs]);
    return targetRef;
};
var NestedMenuItem = forwardRef(function (_a, ref) {
    var _b, _c;
    var children = _a.children, className = _a.className, _d = _a.hideSubmenuOnItemClick, hideSubmenuOnItemClick = _d === void 0 ? true : _d, _e = _a.inset, inset = _e === void 0 ? false : _e, label = _a.label, _f = _a.left, left = _f === void 0 ? false : _f, LeftIcon = _a.LeftIcon, MenuProps = _a.MenuProps, _g = _a.parentMenuOpen, parentMenuOpen = _g === void 0 ? false : _g, RightIcon = _a.RightIcon, MenuItemProps = __rest(_a, ["children", "className", "hideSubmenuOnItemClick", "inset", "label", "left", "LeftIcon", "MenuProps", "parentMenuOpen", "RightIcon"]);
    var theme = useTheme();
    var rtl = theme.direction === 'rtl';
    // Check if right icon is defined from props. If yes, use it. If not, use Right or left chevron based on theme.direction
    if (RightIcon === undefined) {
        RightIcon = rtl ? ChevronLeft16 : ChevronRight16;
    }
    var subMenuRef = useRef(null);
    var defaultRef = useRef(null);
    var menuItemRef = useCombinedRefs(ref, defaultRef);
    var _h = useState(false), subMenuOpen = _h[0], setSubMenuOpen = _h[1];
    var _j = useState(menuItemRef === null || menuItemRef === void 0 ? void 0 : menuItemRef.current), anchorElement = _j[0], setAnchorElement = _j[1];
    var _k = useState(false), shouldOpenLeft = _k[0], setShouldOpenLeft = _k[1];
    var menuRef = useClickOutside(function () {
        // This prevents the menu to hang in DOM if keyboard was used to navigate inside menu.
        setSubMenuOpen(false);
    });
    useEffect(function () {
        var _a, _b;
        if (subMenuOpen && !left) {
            var spaceOnTheRight = window.innerWidth -
                ((_a = menuItemRef === null || menuItemRef === void 0 ? void 0 : menuItemRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().x) -
                ((_b = menuItemRef === null || menuItemRef === void 0 ? void 0 : menuItemRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth);
            setShouldOpenLeft(spaceOnTheRight < SUBMENU_MIN_WIDTH);
        }
    }, [subMenuOpen]);
    var isSubmenuFocused = function () {
        var _a, _b, _c, _d;
        var active = ((_b = (_a = menuItemRef === null || menuItemRef === void 0 ? void 0 : menuItemRef.current) === null || _a === void 0 ? void 0 : _a.ownerDocument) === null || _b === void 0 ? void 0 : _b.activeElement) || false;
        // @ts-expect-error
        for (var _i = 0, _e = (_d = (_c = subMenuRef.current) === null || _c === void 0 ? void 0 : _c.children) !== null && _d !== void 0 ? _d : []; _i < _e.length; _i++) {
            var child = _e[_i];
            if (child === active) {
                return true;
            }
        }
        return false;
    };
    var handleMouseEnter = function (event) {
        event.stopPropagation();
        setAnchorElement(event.currentTarget);
        event.target.focus();
        setSubMenuOpen(true);
    };
    var handleMouseLeave = function () {
        var _a;
        // Close menu and focus parent menu item.
        setSubMenuOpen(false);
        (_a = menuItemRef === null || menuItemRef === void 0 ? void 0 : menuItemRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    var handleClick = function (event) {
        event.stopPropagation();
        // When touching the menu item with touch screen, we don't want to close the menu.
        // @ts-ignore
        if (event.nativeEvent.pointerType !== 'mouse') {
            setSubMenuOpen(true);
        }
        else {
            setSubMenuOpen(!subMenuOpen);
        }
    };
    var handleFocus = function (event) {
        if (event.target === (menuItemRef === null || menuItemRef === void 0 ? void 0 : menuItemRef.current)) {
            setAnchorElement(event.currentTarget);
            setSubMenuOpen(true);
        }
    };
    var handleKeyDown = function (event) {
        var _a, _b, _c, _d, _e;
        var arrowOpen = left ? 'ArrowLeft' : 'ArrowRight';
        var arrowClose = left ? 'ArrowRight' : 'ArrowLeft';
        var length = (_a = subMenuRef.current) === null || _a === void 0 ? void 0 : _a.children.length;
        var firstFocusableChild = (_b = subMenuRef.current) === null || _b === void 0 ? void 0 : _b.querySelector("li:not([class*='Subheader'])");
        if (length) {
            if (event.target === ((_c = subMenuRef.current) === null || _c === void 0 ? void 0 : _c.children[length - 1]) &&
                event.key === 'ArrowDown') {
                event.stopPropagation();
                firstFocusableChild === null || firstFocusableChild === void 0 ? void 0 : firstFocusableChild.focus();
            }
            else if (event.target === firstFocusableChild && event.key === 'ArrowUp') {
                event.stopPropagation();
                (_e = (_d = subMenuRef.current) === null || _d === void 0 ? void 0 : _d.children[length - 1]) === null || _e === void 0 ? void 0 : _e.focus();
            }
            else if (isSubmenuFocused()) {
                event.stopPropagation();
            }
        }
        if (event.key === arrowOpen && !isSubmenuFocused()) {
            if (!subMenuOpen) {
                setSubMenuOpen(true);
                setAnchorElement(event.currentTarget);
            }
            firstFocusableChild === null || firstFocusableChild === void 0 ? void 0 : firstFocusableChild.focus();
            event.stopPropagation();
        }
        else if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') &&
            event.target === (menuItemRef === null || menuItemRef === void 0 ? void 0 : menuItemRef.current)) {
            setSubMenuOpen(false);
        }
        else if (event.key === arrowClose && (menuItemRef === null || menuItemRef === void 0 ? void 0 : menuItemRef.current)) {
            menuItemRef === null || menuItemRef === void 0 ? void 0 : menuItemRef.current.focus();
            setSubMenuOpen(false);
        }
    };
    var handleMenuKeyDown = function (event) {
        var _a;
        if (event.key === 'Escape') {
            event.stopPropagation();
            (_a = menuItemRef === null || menuItemRef === void 0 ? void 0 : menuItemRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            setSubMenuOpen(false);
        }
    };
    // Handle closing the submenu when focus is moved back to the parent menu item
    var onMenuBlur = function (event) {
        var _a;
        if (menuItemRef.current === event.relatedTarget || // If focus moved back to parent.
            ((_a = event.relatedTarget) === null || _a === void 0 ? void 0 : _a.tagName) === 'BUTTON' // If focus moved back to button
        ) {
            setTimeout(function () {
                setSubMenuOpen(false);
            });
        }
    };
    // To pass left prop to NestedMenuItem if this menu is opening to the left.
    var childrenWithProps = Children.map(children, function (child) {
        var _a;
        if (isValidElement(child) &&
            shouldOpenLeft &&
            ((_a = child === null || child === void 0 ? void 0 : child.type) === null || _a === void 0 ? void 0 : _a.displayName) === NestedMenuItem.displayName) {
            return cloneElement(child, { left: true });
        }
        return child;
    });
    // Swap left and right if direction is rtl
    var getDirection = function (side) {
        if (rtl) {
            return side === 'left' ? 'right' : 'left';
        }
        else {
            return side;
        }
    };
    return (React.createElement(MenuItem, __assign({ className: clsx(subMenuOpen && 'Mui-focusVisible', className), onClick: hideSubmenuOnItemClick ? handleClick : undefined, onFocus: handleFocus, onKeyDown: handleKeyDown, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, ref: menuItemRef, style: { outline: 'none', overflow: 'hidden' } }, MenuItemProps),
        LeftIcon && resolveSvgIcon(LeftIcon),
        React.createElement(MenuItemText, __assign({}, { inset: inset }), label),
        resolveSvgIcon(RightIcon),
        React.createElement(Menu, { anchorEl: anchorElement, anchorOrigin: (_b = MenuProps === null || MenuProps === void 0 ? void 0 : MenuProps.anchorOrigin) !== null && _b !== void 0 ? _b : {
                horizontal: shouldOpenLeft || left
                    ? getDirection('left')
                    : getDirection('right'),
                vertical: 'top',
            }, disableAutoFocus: true, disableEnforceFocus: true, disableRestoreFocus: true, onBlur: onMenuBlur, onClose: function () {
                setSubMenuOpen(false);
            }, onKeyDown: handleMenuKeyDown, open: subMenuOpen && parentMenuOpen, ref: menuRef, style: { pointerEvents: 'none', overflow: 'none' }, transformOrigin: (_c = MenuProps === null || MenuProps === void 0 ? void 0 : MenuProps.transformOrigin) !== null && _c !== void 0 ? _c : {
                vertical: 'top',
                horizontal: shouldOpenLeft || left
                    ? getDirection('right')
                    : getDirection('left'),
            } },
            React.createElement("div", { ref: subMenuRef, style: {
                    pointerEvents: 'auto',
                    minWidth: SUBMENU_MIN_WIDTH,
                } }, childrenWithProps))));
});
NestedMenuItem.displayName = 'NestedMenuItem';
export default NestedMenuItem;
